import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Button,
  Alert,
  Box,
  Card,
  CardContent,
  Switch,
  FormControlLabel,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  Paper,
  Fade,
  Zoom,
  Tabs,
  Tab,
  Chip,
  Avatar,
  Divider,
  Grid,
  Stack,
  useMediaQuery
} from '@mui/material';
import {
  ArrowBack,
  Edit,
  Delete,
  Today,
  CheckCircle,
  Cancel,
  MoreVert,
  FilterList,
  Search,
  Close as CloseIcon,
  Add,
  Settings as SettingsIcon,
  Share,
  ContentCopy,
  WhatsApp,
  Email,
  DateRange,
  EventNote,
  Schedule,
  Block,
  CalendarMonth,
  Hotel,
  Restaurant,
  ContentCut,
  SportsSoccer,
  EventSeat,
  Star,
  TrendingUp,
  People,
  Pets
} from '@mui/icons-material';
import axios from 'axios';
import { useBoardData } from '../hooks/useBoardData';
import { useCatAssistant } from '../contexts/CatAssistantContext';
import { useThemeMode } from '../contexts/ThemeContext';
import { useTheme } from '@mui/material/styles';
import CatLoadingSpinner from '../components/CatLoadingSpinner';
import BusinessTour from '../components/MichiTour/BusinessTour';
import ReservationsCalendar from '../components/ReservationsCalendar';
import MassCancellationDialog from '../components/MassCancellationDialog';
import QuickStats from '../components/board/QuickStats';
import PricingSection from '../components/board/PricingSection';
import AmenitiesSection from '../components/board/AmenitiesSection';
import PoliciesSection from '../components/board/PoliciesSection';
import ReservationsSection from '../components/board/ReservationsSection';
import BoardEditDialog from '../components/board/BoardEditDialog';
import ReservationEditDialog from '../components/board/ReservationEditDialog';
import BulkCancelDialog from '../components/board/BulkCancelDialog';
import AgendaSettings from '../components/board/AgendaSettings';
import ShiftStatusTracker from '../components/board/ShiftStatusTracker';
import BoardHeader from '../components/board/BoardHeader';
import LynaAvatar from '../components/LynaAvatar';
import '../cat-theme.css';

interface Board {
  id: string;
  name: string;
  type: string;
  description: string;
  capacity: number;
  slug?: string;
  availabilitySettings: any;
  pricing: any;
  amenities?: string[];
  policies?: any;
  isActive?: boolean;
  businessId?: string;
  business?: { name?: string; slug?: string };
}

interface Reservation {
  id: string;
  customerName: string;
  customerEmail: string;
  startDate: string;
  endDate: string;
  status: string;
}

const BoardDetail: React.FC = () => {
  const { boardId } = useParams<{ boardId: string }>();
  const navigate = useNavigate();
  const { say } = useCatAssistant();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { mode: themeMode, variant } = useThemeMode();
  const isDark = themeMode === 'dark';
  const isTrippy = variant === 'trippy';

  const [pageMode, setPageMode] = useState<'operate' | 'configure'>('operate');
  const [boardEditOpen, setBoardEditOpen] = useState(false);
  const [reservationEditOpen, setReservationEditOpen] = useState(false);
  const [reservationCreateOpen, setReservationCreateOpen] = useState(false);
  const [selectedReservation, setSelectedReservation] = useState<Reservation | null>(null);
  const [bulkCancelOpen, setBulkCancelOpen] = useState(false);
  const [shiftStatusOpen, setShiftStatusOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);

  const {
    board,
    reservations,
    deletedReservations,
    loading,
    error,
    deleteReservation,
    restoreReservation,
    updateBoard,
    updateReservation
  } = useBoardData(boardId || '');

  const [clients, setClients] = useState<any[]>([]);
  const [recurringTemplates, setRecurringTemplates] = useState<any[]>([]);
  const [manualShifts, setManualShifts] = useState<any[]>([]);

  const businessId = board?.businessId || '';

  useEffect(() => {
    if (businessId) {
      fetchClients();
      fetchRecurringTemplates();
      fetchManualShifts();
    }
  }, [businessId]);

  const fetchClients = async () => {
    try {
      const response = await axios.get(`/api/businesses/${businessId}/clients`);
      setClients(response.data);
    } catch (error) { console.error(error); }
  };

  const fetchRecurringTemplates = async () => {
    try {
      const response = await axios.get(`/api/boards/${boardId}/recurring-templates`);
      setRecurringTemplates(response.data);
    } catch (error) { console.error(error); }
  };

  const fetchManualShifts = async () => {
    try {
      const response = await axios.get(`/api/boards/${boardId}/manual-shifts`);
      setManualShifts(response.data);
    } catch (error) { console.error(error); }
  };

  const createClient = async (data: any) => {
    const res = await axios.post(`/api/businesses/${businessId}/clients`, data);
    setClients(prev => [...prev, res.data]);
    return res.data;
  };

  const saveRecurringTemplate = async (data: any) => {
    const res = await axios.post(`/api/boards/${boardId}/recurring-templates`, data);
    setRecurringTemplates(prev => [...prev, res.data]);
    return res.data;
  };

  const deleteRecurringTemplate = async (id: string) => {
    await axios.delete(`/api/recurring-templates/${id}`);
    setRecurringTemplates(prev => prev.filter(t => t.id !== id));
  };

  const saveManualShift = async (data: any) => {
    const res = await axios.post(`/api/boards/${boardId}/manual-shifts`, data);
    setManualShifts(prev => [...prev, res.data]);
    return res.data;
  };

  const deleteManualShift = async (id: string) => {
    await axios.delete(`/api/manual-shifts/${id}`);
    setManualShifts(prev => prev.filter(s => s.id !== id));
  };

  const handleEditReservation = (res: Reservation) => {
    setSelectedReservation(res);
    setReservationEditOpen(true);
  };

  const handleCreateReservation = () => {
    setSelectedReservation(null);
    setReservationCreateOpen(true);
  };

  const handleDeleteReservation = async (id: string) => {
    await deleteReservation(id);
    say('Reserva eliminada 😿', 'alert');
  };

  const handleRestoreReservation = async (id: string) => {
    await restoreReservation(id);
    say('¡Reserva restaurada! 😸', 'success');
  };

  const handleEditBoard = () => setBoardEditOpen(true);

  const handleSaveBoard = async (data: any) => {
    if (await updateBoard(data)) {
      setBoardEditOpen(false);
      say('¡Tablero guardado! 🐱', 'success');
    }
  };

  const handleCloseBoardEdit = () => setBoardEditOpen(false);
  const handleCloseReservationEdit = () => {
    setReservationEditOpen(false);
    setSelectedReservation(null);
  };

  const handleSaveReservation = async (data: any) => {
    if (selectedReservation && await updateReservation(selectedReservation.id, data)) {
      setReservationEditOpen(false);
      setSelectedReservation(null);
      say('¡Reserva actualizada con éxito! 🎉', 'celebration');
    }
  };

  const handleSaveNewReservation = async (data: any) => {
    try {
      const res = await axios.post(`/api/boards/${boardId}/reservations`, data);
      if (res.status === 201) {
        setReservationCreateOpen(false);
        say('¡Nueva reserva creada! 🎊', 'celebration');
        window.location.reload();
      }
    } catch (e) {
      say('Error al crear la reserva 😿', 'alert');
    }
  };

  const handleUpdateReservationStatus = async (id: string, data: any) => {
    await updateReservation(id, data);
    say('Estado de reserva actualizado 📝', 'success');
  };

  const handleOpenShiftStatus = (res: Reservation) => {
    setSelectedReservation(res);
    setShiftStatusOpen(true);
  };

  const handleBulkCancel = async (start: Date, end: Date, reason: string) => {
    await axios.post(`/api/boards/${boardId}/reservations/bulk-cancel`, {
      startDate: start.toISOString(),
      endDate: end.toISOString(),
      reason
    });
    setBulkCancelOpen(false);
    say('Cancelaciones masivas procesadas 📅', 'alert');
    window.location.reload();
  };

  const handleSaveAgendaSettings = async (data: any) => {
    await updateBoard({ availabilitySettings: data });
  };

  const handleToggleActive = async () => {
    const newActive = !board?.isActive;
    await updateBoard({ isActive: newActive });
    say(newActive ? '¡Tablero activado! 🎉' : 'Tablero desactivado ⚠️', newActive ? 'celebration' : 'alert');
  };

  const getShareLink = () => {
    if (board?.business?.slug && board?.slug) {
      return `${window.location.origin}/${board.business.slug}/${board.slug}`;
    }
    return `${window.location.origin}/public/board/${boardId}`;
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(getShareLink());
    say('Link copiado al portapapeles 📋', 'success');
  };

  const handleShareWhatsApp = () => {
    const text = `¡Reserva tu turno en ${board?.name}! 📅\n${getShareLink()}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
  };

  const handleShareEmail = () => {
    const subject = `Reserva en ${board?.name}`;
    const body = `Hola,\n\nTe invito a reservar tu turno en ${board?.name}.\n\nPuedes hacerlo aquí: ${getShareLink()}\n\n¡Te esperamos!`;
    window.open(`mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`, '_blank');
  };

  if (loading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
      <CatLoadingSpinner message="Cargando detalles del tablero..." />
    </Box>
  );

  if (error || !board) return (
    <Container maxWidth="lg">
      <Alert severity="error" sx={{ mt: 4 }}>{error || 'Tablero no encontrado'}</Alert>
    </Container>
  );

  const categorizeReservations = (res: Reservation[]) => {
    const today = new Date();
    today.setHours(0,0,0,0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const nextWeek = new Date(today);
    nextWeek.setDate(nextWeek.getDate() + 7);

    return {
      today: res.filter(r => new Date(r.startDate) >= today && new Date(r.startDate) < tomorrow),
      tomorrow: res.filter(r => new Date(r.startDate) >= tomorrow && new Date(r.startDate) < new Date(tomorrow.getTime() + 86400000)),
      thisWeek: res.filter(r => new Date(r.startDate) >= tomorrow && new Date(r.startDate) < nextWeek),
      upcoming: res.filter(r => new Date(r.startDate) >= nextWeek)
    };
  };

  const categorized = categorizeReservations(reservations);

    return (
        <Box className={isTrippy ? 'theme-trippy' : ''} sx={{ minHeight: '100vh', bgcolor: isDark ? '#0A0A0B' : '#F8FAFC', transition: 'all 0.5s ease' }}>
            <Container maxWidth="xl" sx={{ pt: { xs: 2, md: 4 }, pb: 8 }}>
                {/* Header Section */}
                <Box sx={{ mb: 4 }}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={3}>
                        <Box>
                            <Button
                                startIcon={<ArrowBack />}
                                onClick={() => navigate(`/business/${board.businessId}`)}
                                sx={{ color: 'text.secondary', fontWeight: 800, mb: 1, textTransform: 'none', '&:hover': { bgcolor: 'rgba(0,0,0,0.05)' } }}
                            >
                                Volver al Negocio
                            </Button>
                            <Stack direction="row" spacing={2} alignItems="center">
                                <Avatar sx={{ bgcolor: 'primary.main', width: 56, height: 56, boxShadow: '0 0 20px rgba(99, 102, 241, 0.4)' }}>
                                    {board.type === 'service' ? <Schedule fontSize="large" /> : <EventNote fontSize="large" />}
                                </Avatar>
                                <Box>
                                    <Typography variant="h3" sx={{ fontWeight: 900, letterSpacing: '-1.5px', color: isDark ? '#fff' : '#1e293b', fontSize: { xs: '1.8rem', md: '2.5rem' } }}>
                                        {board.name}
                                    </Typography>
                                    <Typography variant="body1" sx={{ color: 'text.secondary', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Box component="span" sx={{ width: 8, height: 8, bgcolor: board.isActive ? '#10b981' : '#ef4444', borderRadius: '50%' }} />
                                        {board.isActive ? 'Agenda Activa' : 'Agenda Inactiva'} • {board.business?.name}
                                    </Typography>
                                </Box>
                            </Stack>
                        </Box>

                        <Stack direction="row" spacing={2} sx={{ width: { xs: '100%', sm: 'auto' } }}>
                            <Paper sx={{ p: 0.5, borderRadius: 3, bgcolor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)', display: 'flex' }}>
                                <Button
                                    variant={pageMode === 'operate' ? 'contained' : 'text'}
                                    onClick={() => setPageMode('operate')}
                                    sx={{ borderRadius: 2.5, px: 3, fontWeight: 900, textTransform: 'none', color: (pageMode === 'operate' || isDark) ? '#fff' : 'text.secondary' }}
                                >
                                    Gestionar
                                </Button>
                                <Button
                                    variant={pageMode === 'configure' ? 'contained' : 'text'}
                                    onClick={() => setPageMode('configure')}
                                    sx={{ borderRadius: 2.5, px: 3, fontWeight: 900, textTransform: 'none', color: (pageMode === 'configure' || isDark) ? '#fff' : 'text.secondary' }}
                                >
                                    Configurar
                                </Button>
                            </Paper>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <Tooltip title="Compartir Agenda">
                                    <IconButton onClick={() => setShareOpen(true)} sx={{ border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`, borderRadius: 3, color: isDark ? 'white' : 'inherit' }}>
                                        <Share />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        </Stack>
                    </Stack>
                </Box>

                <Fade in timeout={500}>
                    <Box>
                        {pageMode === 'operate' ? (
                            <Grid container spacing={4}>
                                <Grid size={{ xs: 12, lg: 9 }}>
                                    <ReservationsSection
                                        reservations={reservations}
                                        deletedReservations={deletedReservations}
                                        onEdit={handleEditReservation}
                                        onDelete={handleDeleteReservation}
                                        onRestore={handleRestoreReservation}
                                        onUpdateStatus={handleUpdateReservationStatus}
                                        onOpenShiftStatus={handleOpenShiftStatus}
                                        recurringTemplates={recurringTemplates}
                                        onCreateReservation={handleCreateReservation}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, lg: 3 }}>
                                    <Stack spacing={3}>
                                        <QuickStats board={board} reservationsCount={reservations.length} />
                                        <Paper sx={{ p: 3, borderRadius: 5, bgcolor: isDark ? 'rgba(30, 41, 59, 0.4)' : '#fff', border: `1px solid ${isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}` }}>
                                            <Typography variant="h6" sx={{ fontWeight: 900, mb: 2 }}>Próximas Acciones</Typography>
                                            <Stack spacing={1.5}>
                                                <Button fullWidth variant="contained" color="primary" startIcon={<Add />} onClick={handleCreateReservation} sx={{ borderRadius: 3, textTransform: 'none', fontWeight: 900, py: 1.5 }}>Nuevo Turno</Button>
                                                <Button fullWidth variant="outlined" startIcon={<FilterList />} sx={{ borderRadius: 3, textTransform: 'none', fontWeight: 800 }}>Filtrar Vista</Button>
                                                <Button fullWidth variant="outlined" startIcon={<Block />} onClick={() => setBulkCancelOpen(true)} sx={{ borderRadius: 3, textTransform: 'none', fontWeight: 800, color: 'error.main' }}>Cierre Masivo</Button>
                                            </Stack>
                                        </Paper>
                                    </Stack>
                                </Grid>
                            </Grid>
                        ) : (
                            <Box>
                                <Grid container spacing={4}>
                                    <Grid size={{ xs: 12, lg: 4 }}>
                                        <Stack spacing={3}>
                                            <QuickStats board={board} reservationsCount={reservations.length} />
                                            
                                            <Paper sx={{ p: 3, borderRadius: 5, bgcolor: isDark ? 'rgba(30, 41, 59, 0.4)' : '#fff', border: `1px solid ${isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}` }}>
                                                <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                    <Typography variant="h6" sx={{ fontWeight: 900 }}>⚙️ Configuración</Typography>
                                                    <Button 
                                                        size="small" 
                                                        onClick={() => setBoardEditOpen(true)}
                                                        variant="contained"
                                                        sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 800 }}
                                                    >
                                                        Editar Todo
                                                    </Button>
                                                </Box>
                                                <Stack spacing={2}>
                                                    <Box>
                                                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800 }}>URL / SLUG</Typography>
                                                        <Typography variant="body2" sx={{ fontWeight: 700, fontFamily: 'monospace', bgcolor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)', p: 1, borderRadius: 1 }}>
                                                            /{board.slug}
                                                        </Typography>
                                                    </Box>
                                                    <Box>
                                                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800 }}>PRECIO BASE</Typography>
                                                        <Typography variant="h6" sx={{ fontWeight: 900, color: 'success.main' }}>
                                                            ${board.pricing?.basePrice?.toLocaleString() || 0}
                                                        </Typography>
                                                    </Box>
                                                    <Box>
                                                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800 }}>CAPACIDAD</Typography>
                                                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                                            {board.capacity} personas/unid.
                                                        </Typography>
                                                    </Box>
                                                </Stack>
                                            </Paper>

                                            <PricingSection board={board} />
                                            <AmenitiesSection board={board} />
                                            <PoliciesSection board={board} />
                                            
                                            <Paper sx={{ p: 3, borderRadius: 5, bgcolor: isDark ? 'rgba(30,30,30,0.95)' : 'rgba(255,255,255,0.95)' }}>
                                                <Typography variant="h6" color="warning.main" sx={{ fontWeight: 900, mb: 1 }}>🛡️ Estado</Typography>
                                                <FormControlLabel control={<Switch checked={board.isActive} onChange={handleToggleActive} color="success" />} label={board.isActive ? "Agenda Activa ✅" : "Agenda Inactiva ⚠️"} sx={{ mb: 2 }} />
                                                <Button variant="outlined" color="error" startIcon={<Block />} onClick={() => setBulkCancelOpen(true)} fullWidth sx={{ borderRadius: 3, fontWeight: 800 }}>
                                                    Cancelar Reservas Lote
                                                </Button>
                                            </Paper>
                                        </Stack>
                                    </Grid>
                                    <Grid size={{ xs: 12, lg: 8 }}>
                                        <AgendaSettings
                                            settings={board.availabilitySettings || {}}
                                            reservations={reservations}
                                            clients={clients}
                                            recurringTemplates={recurringTemplates}
                                            manualShifts={manualShifts}
                                            onSave={handleSaveAgendaSettings}
                                            onCreateClient={createClient}
                                            onSaveTemplate={saveRecurringTemplate}
                                            onDeleteTemplate={deleteRecurringTemplate}
                                            onSaveShift={saveManualShift}
                                            onDeleteShift={deleteManualShift}
                                            onReservationClick={handleEditReservation}
                                        />
                                    </Grid>
                                </Grid>
                            </Box>
                        )}
                    </Box>
                </Fade>
            </Container>

      <BoardEditDialog open={boardEditOpen} board={board} onClose={handleCloseBoardEdit} onSave={handleSaveBoard} />
      <ReservationEditDialog open={reservationEditOpen} reservation={selectedReservation} businessId={businessId} board={board} onClose={handleCloseReservationEdit} onSave={handleSaveReservation} />
      <ReservationEditDialog open={reservationCreateOpen} reservation={null} businessId={businessId} board={board} onClose={() => setReservationCreateOpen(false)} onSave={handleSaveNewReservation} />
      <BulkCancelDialog open={bulkCancelOpen} onClose={() => setBulkCancelOpen(false)} onConfirm={handleBulkCancel} />
      <ShiftStatusTracker open={shiftStatusOpen} onClose={() => { setShiftStatusOpen(false); setSelectedReservation(null); }} reservation={selectedReservation} onUpdateStatus={handleUpdateReservationStatus} />

      <Dialog open={shareOpen} onClose={() => setShareOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6"><Share color="primary" /> Compartir</Typography>
          <IconButton onClick={() => setShareOpen(false)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField 
              fullWidth 
              label="Enlace" 
              value={getShareLink()} 
              InputProps={{ 
                readOnly: true, 
                endAdornment: <IconButton onClick={handleCopyLink}><ContentCopy /></IconButton> 
              }} 
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button variant="outlined" color="success" startIcon={<WhatsApp />} onClick={handleShareWhatsApp} fullWidth>WhatsApp</Button>
              <Button variant="outlined" color="info" startIcon={<Email />} onClick={handleShareEmail} fullWidth>Email</Button>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default BoardDetail;